import { css } from '@emotion/react';

import { makeStyles } from '@toolbox/style';
import { useId, useUncontrolledProp } from '@toolbox/hooks';
import { keepValueWithinRange } from '@toolbox/utils';
import { Button } from '../Button';
import { Input } from '../Input';
import { Typography } from '../typography';
import { Flex } from '../Flex';

import type { InputProps } from '../Input';
import type { Modify } from '@toolbox/utils';

const useStyles = makeStyles(({ theme }) => ({
  counterContainer: css`
    margin-top: ${theme.spacing(4)};
    width: 100%;
  `,
  input: css`
    text-align: center;
  `,
  button: css`
    border-radius: ${theme.borderRadius.fullRound};
    width: 60px;
    height: 60px;
  `,
  incrementButton: css`
    margin-left: ${theme.spacing(4)};
  `,
  decrementButton: css`
    margin-right: ${theme.spacing(4)};
  `,
  title: css`
    padding: ${theme.spacing(8, 0, 7)};
  `,
}));

type CounterCustomProps = {
  min?: number;
  max?: number;
};

export type CounterProps = Modify<
  InputProps & CounterCustomProps,
  {
    value?: number;
    defaultValue?: number;
    onChange?: (value: number) => void;
  }
>;

export const Counter = (props: CounterProps): JSX.Element => {
  const { label, min, max, ...inputProps } = props;
  const id = useId(props.id);

  const [value, setValue] = useUncontrolledProp(
    props.value,
    props.defaultValue || 0,
    props.onChange,
  );

  const addStep = (direction: 1 | -1): void => {
    setValue((prevValue) => keepValueWithinRange(prevValue + direction, max, min));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const value = Number(e.target.value);
    setValue(keepValueWithinRange(value, max, min));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => e.target.select();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = Number(e.target.value);
    setValue(keepValueWithinRange(value, max, min));
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    //prevent decimal input
    if (e.key === '.' || e.key === 'e') {
      e.preventDefault();
    }
  };

  const classes = useStyles();

  return (
    <div>
      <Typography variant="h3" css={classes.title as any}>
        <label htmlFor={id}>{label}</label>
      </Typography>
      <Flex alignItems="flex-end" css={classes.counterContainer as any}>
        <Button
          data-testid="decrementButton"
          size="medium"
          variant="secondary"
          onClick={() => addStep(-1)}
          css={[classes.button, classes.decrementButton] as any}
        >
          -
        </Button>
        <Input
          {...inputProps}
          data-testid="inputCounter"
          id={id}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          value={value}
          type="number"
          onKeyDown={handleKeyDown}
          css={classes.input as any}
        />
        <Button
          data-testid="incrementButton"
          size="medium"
          variant="secondary"
          onClick={() => addStep(1)}
          css={[classes.button, classes.incrementButton] as any}
        >
          +
        </Button>
      </Flex>
    </div>
  );
};
